import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, Users, FileText, MessageSquare, Star, ArrowLeft,
  Activity, Clock, Shield, CheckSquare, Eye, Download, AlertTriangle,
  UserCheck, UserX, BarChart3, PieChart, Mail, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

function Analytics({ user }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Only admins can access analytics
    if (user && user.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchAnalytics();
  }, [user, navigate]);

  const fetchAnalytics = async () => {
    try {
      const [overviewRes, detailedRes] = await Promise.all([
        fetch(`${API}/analytics/overview`, { credentials: 'include' }),
        fetch(`${API}/analytics/detailed`, { credentials: 'include' })
      ]);
      
      if (overviewRes.ok) {
        setAnalytics(await overviewRes.json());
      }
      if (detailedRes.ok) {
        setDetailedAnalytics(await detailedRes.json());
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#80298F]" />
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'engagement', label: 'Client Engagement', icon: Users },
    { id: 'communication', label: 'Communication', icon: MessageSquare },
    { id: 'workflow', label: 'Workflow', icon: CheckSquare },
    { id: 'satisfaction', label: 'Satisfaction', icon: Star },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  const d = detailedAnalytics || {};

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-[#80298F]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <TrendingUp className="h-7 w-7 text-[#80298F]" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Track client engagement, communication, and portal performance
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 pb-4">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "bg-[#80298F] hover:bg-[#6B2278]" : ""}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Cases</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">{analytics?.total_cases || 0}</p>
                    </div>
                    <FileText className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Cases</p>
                      <p className="text-3xl font-bold text-[#FAA519] mt-1">{analytics?.active_cases || 0}</p>
                    </div>
                    <Activity className="h-10 w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Clients</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">{analytics?.total_clients || 0}</p>
                    </div>
                    <Users className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg Rating</p>
                      <p className="text-3xl font-bold text-[#FAA519] mt-1">
                        {analytics?.avg_satisfaction_rating?.toFixed(1) || '0.0'}
                      </p>
                    </div>
                    <Star className="h-10 w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Cases by Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cases by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.cases_by_status && Object.entries(analytics.cases_by_status).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 capitalize">
                          {status.replace(/_/g, ' ')}
                        </span>
                        <div className="flex items-center space-x-3">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-[#80298F] h-2 rounded-full"
                              style={{ width: `${(count / analytics.total_cases) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-bold text-gray-900 w-6 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cases by Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics?.cases_by_type && Object.entries(analytics.cases_by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="font-medium text-gray-900 capitalize text-sm">{type.replace('_', ' ')}</span>
                        <span className="text-xl font-bold text-[#80298F]">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Communication Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Communication Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <MessageSquare className="h-8 w-8 text-[#80298F] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{analytics?.total_messages || 0}</p>
                    <p className="text-sm text-gray-600">Total Messages</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Star className="h-8 w-8 text-[#FAA519] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{analytics?.total_feedback || 0}</p>
                    <p className="text-sm text-gray-600">Feedback Received</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Mail className="h-8 w-8 text-[#DC3239] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{analytics?.unread_messages || 0}</p>
                    <p className="text-sm text-gray-600">Unread Messages</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Client Engagement Tab */}
        {activeTab === 'engagement' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active Clients</p>
                      <p className="text-3xl font-bold text-green-600 mt-1">
                        {d.client_engagement?.active_clients || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {d.client_engagement?.active_percentage || 0}% of total
                      </p>
                    </div>
                    <UserCheck className="h-10 w-10 text-green-600/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Inactive Clients</p>
                      <p className="text-3xl font-bold text-red-600 mt-1">
                        {d.client_engagement?.inactive_clients || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">No login in 30 days</p>
                    </div>
                    <UserX className="h-10 w-10 text-red-600/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg Session Duration</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">
                        {d.client_engagement?.avg_session_duration_minutes || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">minutes</p>
                    </div>
                    <Clock className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Clients</p>
                      <p className="text-3xl font-bold text-[#FAA519] mt-1">
                        {d.client_engagement?.total_clients || 0}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Feature Usage */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Feature Usage</CardTitle>
                <CardDescription>Most and least accessed portal features</CardDescription>
              </CardHeader>
              <CardContent>
                {d.client_engagement?.feature_usage && Object.keys(d.client_engagement.feature_usage).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(d.client_engagement.feature_usage)
                      .sort((a, b) => b[1] - a[1])
                      .map(([feature, count]) => (
                        <div key={feature} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {feature.replace(/_/g, ' ')}
                          </span>
                          <div className="flex items-center space-x-3">
                            <div className="w-40 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-[#80298F] h-2 rounded-full"
                                style={{ width: `${Math.min((count / 100) * 100, 100)}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-bold text-gray-900 w-10 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No feature usage data available yet</p>
                )}
              </CardContent>
            </Card>

            {/* Login Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Login Trends (Last 30 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                {d.client_engagement?.login_trends && Object.keys(d.client_engagement.login_trends).length > 0 ? (
                  <div className="space-y-2">
                    <div className="h-40 flex items-end gap-1 px-2">
                      {Object.entries(d.client_engagement.login_trends)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .slice(-14)
                        .map(([date, count]) => {
                          const maxCount = Math.max(...Object.values(d.client_engagement.login_trends));
                          const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                          return (
                            <div key={date} className="flex-1 flex flex-col items-center min-w-[20px] max-w-[60px]">
                              <span className="text-xs text-gray-600 mb-1">{count}</span>
                              <div 
                                className="w-full bg-[#80298F] rounded-t transition-all hover:bg-[#6b2279]"
                                style={{ height: `${Math.max(height, 8)}%`, minHeight: '8px' }}
                                title={`${date}: ${count} logins`}
                              ></div>
                            </div>
                          );
                        })}
                    </div>
                    <div className="flex gap-1 px-2">
                      {Object.entries(d.client_engagement.login_trends)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .slice(-14)
                        .map(([date]) => (
                          <div key={date} className="flex-1 min-w-[20px] max-w-[60px] text-center">
                            <span className="text-xs text-gray-500">{date.slice(5)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No login data available yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Communication Tab */}
        {activeTab === 'communication' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Avg Response Time</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">
                        {d.communication?.avg_response_time_hours || 0}h
                      </p>
                    </div>
                    <Clock className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Unread by Clients</p>
                      <p className="text-3xl font-bold text-[#FAA519] mt-1">
                        {d.communication?.unread_by_clients || 0}
                      </p>
                    </div>
                    <Mail className="h-10 w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Unread by Staff</p>
                      <p className="text-3xl font-bold text-[#DC3239] mt-1">
                        {d.communication?.unread_by_staff || 0}
                      </p>
                    </div>
                    <Mail className="h-10 w-10 text-[#DC3239]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Delayed Responses</p>
                      <p className="text-3xl font-bold text-orange-600 mt-1">
                        {d.communication?.delayed_responses || 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">&gt;24 hours</p>
                    </div>
                    <AlertTriangle className="h-10 w-10 text-orange-600/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Total Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <MessageSquare className="h-12 w-12 text-[#80298F] mx-auto mb-3" />
                  <p className="text-4xl font-bold text-gray-900">{d.communication?.total_messages || 0}</p>
                  <p className="text-gray-600 mt-1">messages exchanged</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Workflow Tab */}
        {activeTab === 'workflow' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Tasks</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">
                        {d.workflow?.total_tasks || 0}
                      </p>
                    </div>
                    <CheckSquare className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Completed Tasks</p>
                      <p className="text-3xl font-bold text-green-600 mt-1">
                        {d.workflow?.completed_tasks || 0}
                      </p>
                    </div>
                    <CheckSquare className="h-10 w-10 text-green-600/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Completion Rate</p>
                      <p className="text-3xl font-bold text-[#FAA519] mt-1">
                        {d.workflow?.completion_rate || 0}%
                      </p>
                    </div>
                    <PieChart className="h-10 w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Document Views</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">
                        {d.case_updates?.document_views || 0}
                      </p>
                    </div>
                    <Eye className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Case Updates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-900">Total Cases</span>
                      <span className="text-xl font-bold text-[#80298F]">{d.case_updates?.total_cases || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-900">Updates (30 days)</span>
                      <span className="text-xl font-bold text-[#FAA519]">{d.case_updates?.updates_last_30_days || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Document Access</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-[#80298F]" />
                        <span className="font-medium text-gray-900">Views</span>
                      </div>
                      <span className="text-xl font-bold text-[#80298F]">{d.case_updates?.document_views || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Download className="h-5 w-5 text-[#FAA519]" />
                        <span className="font-medium text-gray-900">Downloads</span>
                      </div>
                      <span className="text-xl font-bold text-[#FAA519]">{d.case_updates?.document_downloads || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Satisfaction Tab */}
        {activeTab === 'satisfaction' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Average Rating</p>
                      <p className="text-3xl font-bold text-[#FAA519] mt-1">
                        {d.satisfaction?.avg_rating || 0}
                      </p>
                      <div className="flex mt-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-4 w-4 ${
                              star <= (d.satisfaction?.avg_rating || 0)
                                ? 'text-[#FAA519] fill-[#FAA519]'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <Star className="h-10 w-10 text-[#FAA519]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Feedback</p>
                      <p className="text-3xl font-bold text-[#80298F] mt-1">
                        {d.satisfaction?.total_feedback || 0}
                      </p>
                    </div>
                    <MessageSquare className="h-10 w-10 text-[#80298F]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Submission Rate</p>
                      <p className="text-3xl font-bold text-green-600 mt-1">
                        {d.satisfaction?.feedback_submission_rate || 0}%
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-green-600/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Rating Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rating Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = d.satisfaction?.rating_distribution?.[rating] || 0;
                    const total = d.satisfaction?.total_feedback || 1;
                    const percentage = (count / total) * 100;
                    return (
                      <div key={rating} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-16">
                          <span className="font-medium">{rating}</span>
                          <Star className="h-4 w-4 text-[#FAA519] fill-[#FAA519]" />
                        </div>
                        <div className="flex-1 bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-[#FAA519] h-3 rounded-full"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Failed Logins (7 days)</p>
                      <p className="text-3xl font-bold text-[#DC3239] mt-1">
                        {d.security?.failed_logins_7_days || 0}
                      </p>
                    </div>
                    <Shield className="h-10 w-10 text-[#DC3239]/20" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Failed Logins (30 days)</p>
                      <p className="text-3xl font-bold text-orange-600 mt-1">
                        {d.security?.failed_logins_30_days || 0}
                      </p>
                    </div>
                    <AlertTriangle className="h-10 w-10 text-orange-600/20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Portal Adoption */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Portal Adoption</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Users className="h-8 w-8 text-[#80298F] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{d.adoption?.invitations_sent || 0}</p>
                    <p className="text-sm text-gray-600">Invitations Sent</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <UserCheck className="h-8 w-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{d.adoption?.invitations_accepted || 0}</p>
                    <p className="text-sm text-gray-600">Invitations Accepted</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <TrendingUp className="h-8 w-8 text-[#FAA519] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-900">{d.adoption?.invitation_conversion_rate || 0}%</p>
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

export default Analytics;
